<template>
  <div class="cost-report-page">
    <div class="header">
      <h1>Cost Report</h1>
      <div class="date-selector">
        <select v-model="selectedMonth" class="select">
          <option v-for="(m, i) in months" :key="i" :value="m">{{ m }}</option>
        </select>
        <select v-model="selectedYear" class="select">
          <option v-for="(y, i) in years" :key="i" :value="y">{{ y }}</option>
        </select>
        <!-- Retrieve button removed. Retrieval now happens on month/year change. -->
      </div>
    </div>
    <template v-if="loading">
      <div class="loader-container">
        <InlineLoader width="60px" height="60px" border="8px" />
      </div>
    </template>
    <template v-else-if="costReport">
      <div class="cost-summary-row">
        <div class="cost-summary-card">
          <div class="cost-summary-table">
            <div class="cost-row"><span class="cost-key">General Cloud Costs</span><span class="cost-value">${{ costReport.general_cloud_costs ? costReport.general_cloud_costs.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-row"><span class="cost-key">Database Costs</span><span class="cost-value">${{ costReport.database_cost ? costReport.database_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-row"><span class="cost-key">S3 Costs</span><span class="cost-value">${{ costReport.s3_cost ? costReport.s3_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-row"><span class="cost-key">EC2 Costs</span><span class="cost-value">${{ costReport.ec2_cost ? costReport.ec2_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-divider-gray-visible"></div>
            <div class="cost-row"><span class="cost-key cost-indent">Web Server Cost</span><span class="cost-value cost-indent">${{ costReport.web_server_cost ? costReport.web_server_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-row"><span class="cost-key cost-indent">AI Workflows by Users</span><span class="cost-value cost-indent">${{ costReport.ai_service_cost ? costReport.ai_service_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-row"><span class="cost-key cost-indent">Non-Studio EC2</span><span class="cost-value cost-indent">${{ costReport.non_studio_ec2_cost ? costReport.non_studio_ec2_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-divider-gray-visible"></div>
            <div class="cost-row"><span class="cost-key">TAX</span><span class="cost-value">${{ costReport.tax_cost ? costReport.tax_cost.toFixed(2) : '0.00' }}</span></div>
            <div class="cost-divider-gray-visible"></div>
            <div class="cost-row"><span class="cost-key">Total Cost</span><span class="cost-value">${{ costReport.aws_total_cost ? costReport.aws_total_cost.toFixed(2) : '0.00' }}</span></div>
          </div>
        </div>
        <div class="user-cost-bar-card">
          <h3 style="margin-bottom: 10px;">Top Users</h3>
          <svg :width="chartWidth" :height="sortedUserRows.length * barHeight + 30">
            <g v-for="(user, idx) in sortedUserRows" :key="user.username">
              <rect :x="labelWidth" :y="idx * barHeight + 10" :width="barWidth(user.totalCost)" :height="barHeight - 8" fill="#4a90e2" rx="4" />
              <text :x="labelWidth - 8" :y="idx * barHeight + barHeight/2 + 10" font-size="13" alignment-baseline="middle" text-anchor="end" fill="#222">{{ user.username }}</text>
              <text :x="labelWidth + barWidth(user.totalCost) + 8" :y="idx * barHeight + barHeight/2 + 10" font-size="13" alignment-baseline="middle" fill="#222">${{ user.totalCost.toFixed(2) }}</text>
            </g>
          </svg>
        </div>
      </div>
      <div class="user-costs">
        <div style="display: flex; align-items: center; border-top: 2px solid #ccc; border-bottom: 2px solid #ccc; padding: 8px 0; margin-bottom: 12px;">
          <h3 style="margin: 0; flex: 1;">AI Workflows by Users</h3>
          <span style="min-width: 120px; text-align: right; font-weight: bold;">
            ${{ userRows && userRows.length > 0 ? userRows.reduce((sum, u) => sum + u.totalCost, 0).toFixed(2) : '0.00' }}
          </span>
        </div>
        <div v-for="user in userRows" :key="user.username" class="user-row">
          <div class="user-header" @click="expandedUsers.includes(user.username) ? expandedUsers.splice(expandedUsers.indexOf(user.username), 1) : expandedUsers.push(user.username)">
            <span class="collapse-icon">{{ expandedUsers.includes(user.username) ? '▼' : '▶' }}</span>
            <span class="user-email">{{ user.username }}</span>
            <span class="user-orgid">Org ID: {{ user.entries[0]?.org_id }}</span>
            <span class="user-cost" style="margin-left:auto; text-align:right; min-width:80px; display:inline-block;">${{ user.totalCost.toFixed(2) }}</span>
          </div>
          <div v-if="expandedUsers.includes(user.username)" class="user-details">
            <table class="user-cost-table">
              <thead>
                <tr>
                  <th style="width:200px; max-width:200px;">Date</th>
                  <th style="text-align:left;">Description</th>
                  <th>Cost Type</th>
                  <th>Server Type</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th style="text-align:right;">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, idx) in user.entries" :key="idx">
                  <td style="width:200px; max-width:200px; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ entry.date ? new Date(entry.date).toLocaleString() : '' }}</td>
                  <td style="text-align:left; max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ entry.description }}</td>
                  <td>{{ entry.cost_type }}</td>
                  <td>{{ entry.server_type || '' }}</td>
                  <td>{{ entry.duration.toFixed(2) }}</td>
                  <td>${{ entry.price.toFixed(2) }}</td>
                  <td style="text-align:right;">${{ entry.cost.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div>No data available.</div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import InlineLoader from '@/components/InlineLoader.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

const columns = ref([]);
const costReport = ref(null);
const userRows = ref([]);
const expandedUsers = ref([]);
const loading = ref(true);
const errorMessage = ref("");
const dataConnect = new DatastoreConnect();
const $toast = useToast();

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
const selectedMonth = ref(months[new Date().getMonth()]);
const selectedYear = ref(currentYear);

import { watch } from 'vue';
watch(selectedMonth, () => fetchCostReport());
watch(selectedYear, () => fetchCostReport());

async function fetchCostReport() {
  loading.value = true;
  errorMessage.value = "";
  costReport.value = null;
  userRows.value = [];
  expandedUsers.value = [];
  try {
    const monthInt = months.indexOf(selectedMonth.value) + 1;
    const yearInt = selectedYear.value;
    const response = await dataConnect.api('admin.costs', { month: monthInt, year: yearInt });
    if (response && response.error && response.error.message) {
      errorMessage.value = response.error.message;
      $toast.error(response.error.message, { position: 'top' });
    } else if (response && response.result) {
      const r = response.result;
      costReport.value = r;
      // Parse user costs and sort by username ascending
      const users = Object.entries(r.user_costs || {}).map(([username, entries]) => {
        const totalCost = entries.reduce((sum, entry) => sum + (entry.cost || 0), 0);
        return {
          username,
          totalCost,
          entries
        };
      });
      userRows.value = users.sort((a, b) => a.username.localeCompare(b.username));
    }
  } catch (error) {
    errorMessage.value = 'Failed to fetch cost report.';
    $toast.error('Failed to fetch cost report.', { position: 'top' });
    costReport.value = null;
    userRows.value = [];
    expandedUsers.value = [];
    console.error('Failed to fetch cost report:', error);
  } finally {
    loading.value = false;
  }
}

const chartWidth = 420;
const labelWidth = 120;
const barHeight = 28;
const maxUserCost = computed(() => userRows.value.length > 0 ? Math.max(...userRows.value.map(u => u.totalCost)) : 1);
const barMaxWidth = computed(() => chartWidth - labelWidth - 60);
function barWidth(cost) {
  if (!maxUserCost.value) return 0;
  return Math.max(2, (cost / maxUserCost.value) * barMaxWidth.value);
}

const sortedUserRows = computed(() => {
  return [...userRows.value].sort((a, b) => b.totalCost - a.totalCost).slice(0, 8);
});

onMounted(fetchCostReport);
</script>

<style scoped>
.cost-report-page {
  padding: 32px;
  max-height: 100vh;
  overflow-y: auto;
}
.header {
  font-size: 1.2rem;
  font-weight: 700;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.date-selector {
  width: 380px;
  display: flex;
  flex-direction: row;
  margin-left: 20px;
  font-size: 1rem;
  font-weight: 500;
}
.date-selector select + select {
  margin-left: 20px;
}
.date-selector button {
  margin-left: 20px;
}
.cost-report-table {
  width: 100%;
  border-collapse: collapse;
}
.cost-report-table th, .cost-report-table td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: left;
}
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
}

.cost-summary {
  margin-bottom: 24px;
  font-size: 0.8rem;
}
.cost-summary-card {
  background: #f7fafd;
  border-radius: 12px;
  box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08);
  padding: 16px 20px;
  margin-bottom: 12px;
}
.cost-summary-table {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 6px 18px;
}
.cost-row {
  display: contents;
}
.cost-key {
  font-weight: 600;
  text-align: left;
  font-size: 0.98rem;
}
.cost-value {
  text-align: right;
  font-size: 0.98rem;
}
.cost-divider {
  grid-column: 1 / -1;
  border-bottom: 2px solid #adc1cf;
  margin: 8px 0;
  height: 0;
}
.cost-divider-gray {
  grid-column: 1 / -1;
  border-bottom: 2px solid #bbb;
  margin: 8px 0;
  height: 0;
}
.cost-divider-gray-visible {
  grid-column: 1 / -1;
  height: 2px;
  background: #bbb;
  margin: 0;
}

.user-row {
  border-bottom: 1px solid #eee;
  padding: 2px 0;
}
.user-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  font-size: 1rem;
  background: #adc1cf;
  border-radius: 6px;
  padding: 4px 12px;
}
.collapse-icon {
  font-size: 1.1rem;
  width: 24px;
  text-align: center;
}
.user-email {
  font-weight: 600;

}
.user-cost {
  font-weight: 500;
  color: #01070e;
}
.user-details {
  margin-left: 10px;
  margin-top: 4px;
  font-size: 0.98rem;
}
.user-cost-table {
  width: 100%;

  border-radius: 8px;
  margin-top: 2px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
  background: #f7fafd;
  color: #222;
}

.user-cost-table thead > tr > th {
  font-weight: 600;
  font-size: 1rem;
  background: #e3ecf7;
  padding: 4px 4px;
  border-bottom: 2px solid #adc1cf;
}
.user-cost-table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, .05);
}
.user-cost-table tbody tr {
  height: 32px !important;
}
.user-cost-table td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
  line-height: 1.3 !important;
  border: none;
}
.user-orgid {
  margin-left: 16px;
  font-size: 0.98rem;
  color: #2b2b2b;
}
.user-cost-table th:nth-child(2),
.user-cost-table td:nth-child(2) {
  text-align: center;
}
.user-cost-table th:nth-child(5),
.user-cost-table td:nth-child(5),
.user-cost-table th:nth-child(6),
.user-cost-table td:nth-child(6),
.user-cost-table th:nth-child(4),
.user-cost-table td:nth-child(4) {
  text-align: right;
}
.user-cost-table th:nth-child(3),
.user-cost-table td:nth-child(3) {
  max-width: 200px;
  width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
.cost-key.cost-indent, .cost-value.cost-indent {
  margin-left: 16px;
  font-size: 0.95rem;
}
.user-cost-bar-card {
  background: #f7fafd;
  border-radius: 12px;
  box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08);
  padding: 18px 24px 4px 12px;

  height: 100%;


}
.cost-summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: stretch;

}
.user-cost-bar-card {
  min-width: 300px;
  max-width: 600px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  height: 100%;
  min-height: 320px;
 
}
.cost-summary-card {
  min-height: 320px;
  flex-grow: 1;
  min-width: 300px;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  height: 100%;
}

</style>
